import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { DEFAULT_MOMENTS } from "@/data/superCerveau";
import { Panel } from "./scShared";

const Scale = ({ value, onPick, hidden, testid, disabled, scale }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {scale.map((e) => {
      const active = value === e.id;
      return (
        <button key={e.id} disabled={disabled} onClick={() => onPick(e.id)} data-testid={`${testid}-${e.id}`} aria-label={e.label} title={e.label}
          className={`w-10 h-10 rounded-2xl text-xl flex items-center justify-center border-2 transition-all active:scale-95 disabled:cursor-default ${active && !hidden ? "scale-110 shadow" : "bg-white"}`}
          style={active && !hidden ? { background: e.color, borderColor: e.color } : { borderColor: "var(--border)" }}>
          {hidden && active ? "❓" : e.emoji}
        </button>
      );
    })}
  </div>
);

const MomentRow = ({ m, data, setData, child, parent, onRemove, custom, scale, xpDevinette }) => {
  const d = data || {};
  const both = d.parent !== undefined && d.enfant !== undefined;
  const match = both && d.parent === d.enfant;
  const final = match ? d.enfant : d.final;
  const fe = scale.find((e) => e.id === final);
  return (
    <div className={`rounded-2xl border-2 p-3 sm:p-4 transition-colors ${both ? (match ? "border-[#7CC6A6] bg-[#E4F5EC]/60" : final !== undefined ? "border-[#F2B84B] bg-[#FFF3D6]/60" : "border-[#E0705C]/60 bg-white") : "border-[var(--border)] bg-white"}`} data-testid={`sc-moment-${m.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-bold text-[var(--ink)] text-sm sm:text-base">{m.emoji} {m.label}</div>
        <div className="flex items-center gap-2">
          {fe && <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: fe.color }} data-testid={`sc-moment-${m.id}-final`}>{fe.emoji} {fe.label}</span>}
          {custom && <button onClick={onRemove} aria-label="Retirer ce temps" data-testid={`sc-moment-${m.id}-remove`} className="text-[var(--ink-soft)] hover:text-[#B4483A]"><X size={14} /></button>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#237572] mb-1">1. {parent} devine {d.parent !== undefined && !both ? "· devinette posée 🎲" : ""}</div>
          <Scale value={d.parent} hidden={d.parent !== undefined && !both} onPick={(v) => !both && setData({ ...d, parent: v })} disabled={both} testid={`sc-moment-${m.id}-parent`} scale={scale} />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#B8781A] mb-1">2. {child} répond</div>
          <Scale value={d.enfant} onPick={(v) => !both && setData({ ...d, enfant: v })} disabled={both || d.parent === undefined} testid={`sc-moment-${m.id}-enfant`} scale={scale} />
        </div>
      </div>
      {both && (
        <div className="mt-3 text-sm animate-fade-up" data-testid={`sc-moment-${m.id}-result`}>
          {match ? (
            <span className="font-bold text-[#2F7A55]">🎯 Devinette réussie ! Vous êtes d'accord.{xpDevinette > 0 && ` +${xpDevinette} XP`}</span>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[var(--ink)]">Pas la même vision ({parent} : {scale.find((e) => e.id === d.parent)?.emoji} · {child} : {scale.find((e) => e.id === d.enfant)?.emoji}). On en parle et on se met d'accord :</span>
              <Scale value={d.final} onPick={(v) => setData({ ...d, final: v })} testid={`sc-moment-${m.id}-final-pick`} scale={scale} />
            </div>
          )}
          <button onClick={() => setData({})} className="mt-2 text-xs text-[var(--gold)] hover:underline" data-testid={`sc-moment-${m.id}-reset`}>Recommencer ce temps</button>
        </div>
      )}
    </div>
  );
};

export default function ScTempsForts({ day, setDay, moments, setMoments, childName, parentName, scale, xpDevinette = 1 }) {
  const [label, setLabel] = useState("");
  const child = childName || "L'enfant";
  const parent = parentName || "Le parent";
  const setMoment = (id, v) => setDay({ moments: { ...(day.moments || {}), [id]: v } });
  const add = () => { const l = label.trim(); if (!l) return; setMoments([...moments, { id: `c-${Date.now().toString(36)}`, label: l, emoji: "⭐", custom: true }]); setLabel(""); };
  const done = moments.filter((m) => { const d = (day.moments || {})[m.id]; return d && d.parent !== undefined && d.enfant !== undefined && (d.parent === d.enfant || d.final !== undefined); }).length;
  const matches = moments.filter((m) => { const d = (day.moments || {})[m.id]; return d && d.parent !== undefined && d.parent === d.enfant; }).length;

  return (
    <Panel title="Étape 1 — « Les temps forts de la journée »" sub={`Une devinette à deux : ${parent} devine d'abord comment ${child} a vécu chaque moment, puis ${child} répond. Si vous n'êtes pas d'accord, vous en parlez et choisissez ensemble.`} color="mint" testid="sc-step-temps-forts">
      <div className="flex flex-wrap gap-2 mb-4 text-xs font-bold">
        <span className="px-3 py-1.5 rounded-full bg-[#E4F5EC] text-[#2F7A55]" data-testid="sc-moments-done">{done}/{moments.length} temps appréciés</span>
        <span className="px-3 py-1.5 rounded-full bg-[#FFF3D6] text-[#B8781A]" data-testid="sc-moments-matches">🎯 {matches} devinette(s) réussie(s)</span>
        <span className="px-3 py-1.5 rounded-full bg-[var(--sand)] text-[var(--ink-soft)] font-medium">{scale.map((e) => `${e.emoji} ${e.label}`).join(" · ")}</span>
      </div>
      <div className="space-y-3">
        {moments.map((m) => <MomentRow key={m.id} m={m} data={(day.moments || {})[m.id]} setData={(v) => setMoment(m.id, v)} child={child} parent={parent} scale={scale} xpDevinette={xpDevinette} custom={m.custom} onRemove={() => setMoments(moments.filter((x) => x.id !== m.id))} />)}
      </div>
      <div className="mt-4 flex gap-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ajouter un temps si besoin (ex. Trajet, Douche, Écrans…)" data-testid="sc-moment-add-input"
          className="flex-1 rounded-full border border-[var(--border)] bg-white px-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[#7CC6A6]" />
        <button onClick={add} data-testid="sc-moment-add" className="w-11 h-11 rounded-full bg-[#7CC6A6] text-white flex items-center justify-center shrink-0"><Plus size={18} /></button>
      </div>
      {moments.some((m) => m.custom) && <button onClick={() => setMoments(DEFAULT_MOMENTS)} className="mt-2 text-xs text-[var(--gold)] hover:underline" data-testid="sc-moments-reset">Rétablir la liste des temps par défaut</button>}
    </Panel>
  );
}
